import { Link } from 'react-router-dom'
import { Head } from 'vite-react-ssg'
import { getCanonicalUrl } from '../utils/canonical'
import {
  Target,
  History,
  Shield,
  CheckCircle,
  Users,
  Lock,
  Zap,
  Heart,
  Github,
  ExternalLink,
  Code,
  Globe,
  FileCheck,
  RefreshCw,
} from 'lucide-react'

export default function AboutPage() {
  const trustPoints = [
    {
      title: 'Transparency & Open Source',
      description: 'Our core code and test rules are public. You can inspect, audit, and contribute. We don\'t hide behind black-box results.',
      icon: Code,
      link: { text: 'See how we test via our WCAG validator', href: '/wcag-validator' },
    },
    {
      title: 'Based on Recognized Standards',
      description: 'Our checks reference global accessibility standards: WCAG (versions 2.0, 2.1, and evolving to 2.2), Section 508, EN 301 549, and others. We clearly distinguish when a violation is Known, Likely, or Potential, so you understand which issues need human review.',
      icon: Shield,
    },
    {
      title: 'Independent & Nonprofit-Minded',
      description: 'We are not beholden to large vendors or paywalls. Our free tool is designed to lower entry barriers for accessibility. We don\'t sell your scanned URLs or share them. You remain in control.',
      icon: Heart,
    },
    {
      title: 'Ongoing Maintenance & Updates',
      description: 'We regularly update our checks to reflect new WCAG criteria and evolving best practices. As new accessibility research emerges, we incorporate it.',
      icon: RefreshCw,
    },
  ]

  const qualityStandards = [
    {
      area: 'Rule Coverage',
      standard: '~310 OAC check rules',
      ensures: 'Broad coverage across recognized criteria',
    },
    {
      area: 'Multi-standard support',
      standard: 'WCAG 2.0, 2.1, evolving 2.2 + Section 508, EN 301 549',
      ensures: 'Tool is useful for global audiences',
    },
    {
      area: 'Result classification',
      standard: 'Known / Likely / Potential',
      ensures: 'Clarity on confidence level of each finding',
    },
    {
      area: 'Manual review awareness',
      standard: '"Potential problems require human judgment"',
      ensures: 'Users understand limits of automation',
    },
    {
      area: 'Code integrity',
      standard: 'Open source, peer review',
      ensures: 'Trust via public scrutiny',
    },
    {
      area: 'Frequent updates',
      standard: 'Regular revision of rules',
      ensures: 'Tool stays current with evolving standards',
    },
  ]

  const expectations = [
    {
      title: 'Instant feedback',
      description: 'Enter your URL (or HTML) → get an Accessibility Score + detailed result.',
      icon: Zap,
    },
    {
      title: 'Clear guidance',
      description: 'We show which rule failed, potential solutions, and where in your HTML/CSS the issue occurred.',
      icon: FileCheck,
    },
    {
      title: 'Confidence levels',
      description: 'Some issues require human review (categorized as Potential).',
      icon: CheckCircle,
    },
    {
      title: 'Iterative improvement',
      description: 'You can re-scan anytime to track progress.',
      icon: RefreshCw,
    },
  ]

  return (
    <>
      <Head>
        <title>About achecker.ca – Mission, Standards & Trust</title>
        <meta name="description" content="Learn why achecker.ca is the #1 free web accessibility tool: our mission, transparent methods, standards used, and commitment to quality and trust." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={getCanonicalUrl('/about')} />
      </Head>

      <div className="space-y-12">
        {/* Hero Section */}
        <section className="text-center py-12 bg-gradient-to-br from-green-50 to-teal-100 rounded-2xl">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            About achecker.ca – Our Mission, Values & Quality Promise
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-4xl mx-auto">
            Building a more accessible web, one check at a time. Free, transparent, and committed to global standards.
          </p>
        </section>

        {/* Our Mission */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <Target className="w-8 h-8 text-green-600" />
            <h2 className="text-3xl font-bold text-gray-900">Our Mission: Making the Web Accessible for Everyone</h2>
          </div>
          <div className="bg-white rounded-lg p-8 shadow-sm border border-gray-200">
            <p className="text-lg text-gray-700 mb-6">
              We believe that accessibility is a fundamental human right. No one should be excluded from online content because of disability or technical barriers. That's why achecker.ca offers a fully free, instant web accessibility check—so site owners and dev teams everywhere can begin improving without upfront cost.
            </p>
            <p className="text-gray-700">
              <Link to="/" className="text-green-600 hover:text-green-700 font-medium inline-flex items-center gap-1">
                Try our free accessibility checker now
                <Zap className="w-4 h-4" />
              </Link>
            </p>
          </div>
        </section>

        {/* Our Roots & Background */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <History className="w-8 h-8 text-green-600" />
            <h2 className="text-3xl font-bold text-gray-900">Our Roots & Background</h2>
          </div>
          <div className="bg-white rounded-lg p-8 shadow-sm border border-gray-200">
            <div className="space-y-4 text-gray-700">
              <p>
                achecker.ca is built on the <strong>AChecker open-source project</strong>: an automated interactive web content accessibility checker.
                <a
                  href="https://github.com/inclusive-design/AChecker"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-green-600 hover:text-green-700 inline-flex items-center gap-1 ml-1"
                >
                  <Github className="w-4 h-4" />
                  GitHub
                  <ExternalLink className="w-3 h-3" />
                </a>
              </p>
              <p>
                AChecker has been around since circa <strong>2005</strong>, aiming for transparency, customizability, and free access.
              </p>
              <p>
                The core engine uses <strong>Open Accessibility Checks (OAC)</strong>—a collection of test rules drawn from recognized accessibility standards worldwide (currently <strong>~310 checks</strong>).
              </p>
              <p>
                The project is maintained openly under the <strong>GPL-2.0 license</strong>, ensuring transparency and community involvement.
              </p>
            </div>
          </div>
        </section>

        {/* Why You Can Trust Us */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <Shield className="w-8 h-8 text-green-600" />
            <h2 className="text-3xl font-bold text-gray-900">Why You Can Trust Us</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {trustPoints.map((point, index) => {
              const Icon = point.icon
              return (
                <div key={index} className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">{index + 1}. {point.title}</h3>
                      <p className="text-gray-700 text-sm">{point.description}</p>
                      {point.link && (
                        <Link
                          to={point.link.href}
                          className="text-green-600 hover:text-green-700 text-sm font-medium inline-flex items-center gap-1 mt-2"
                        >
                          {point.link.text}
                          <ExternalLink className="w-3 h-3" />
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </section>

        {/* Our Quality Standards & Methodology */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <CheckCircle className="w-8 h-8 text-green-600" />
            <h2 className="text-3xl font-bold text-gray-900">Our Quality Standards & Methodology</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-lg shadow-sm border border-gray-200">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-900">Area</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-900">Standard / Method</th>
                  <th className="px-6 py-3 text-left text-sm font-medium text-gray-900">What It Ensures</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {qualityStandards.map((item, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{item.area}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">{item.standard}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">{item.ensures}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Our Team & Contributors */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <Users className="w-8 h-8 text-green-600" />
            <h2 className="text-3xl font-bold text-gray-900">Our Team & Contributors</h2>
          </div>
          <div className="bg-white rounded-lg p-8 shadow-sm border border-gray-200">
            <div className="space-y-4 text-gray-700">
              <p>
                We welcome community contributions via GitHub (issues, pull requests).
                <a
                  href="https://github.com/inclusive-design/AChecker"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-green-600 hover:text-green-700 inline-flex items-center gap-1 ml-1"
                >
                  <Github className="w-4 h-4" />
                  Contribute on GitHub
                  <ExternalLink className="w-3 h-3" />
                </a>
              </p>
              <p>
                Our team reviews community submissions and validates new test rules before deployment.
              </p>
              <p>
                We maintain issue tracking, changelogs, and transparency around updates.
              </p>
            </div>
          </div>
        </section>

        {/* How We Protect Your Privacy */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <Lock className="w-8 h-8 text-green-600" />
            <h2 className="text-3xl font-bold text-gray-900">How We Protect Your Privacy</h2>
          </div>
          <div className="bg-white rounded-lg p-8 shadow-sm border border-gray-200">
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span>All scans are <strong>anonymous by default</strong>.</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span>We do <strong>not store or share</strong> your scanned URLs or results—unless you explicitly save them.</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span>We may log <strong>anonymized aggregate data</strong> to improve the tool, but never link to personal info.</span>
              </li>
            </ul>
          </div>
        </section>

        {/* What to Expect When Using Our Tools */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <Zap className="w-8 h-8 text-green-600" />
            <h2 className="text-3xl font-bold text-gray-900">What to Expect When Using Our Tools</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {expectations.map((item, index) => {
              const Icon = item.icon
              return (
                <div key={index} className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{item.title}</h3>
                    <p className="text-gray-700 text-sm">{item.description}</p>
                  </div>
                </div>
              )
            })}
          </div>
        </section>

        {/* Join Us in Building an Inclusive Web */}
        <section className="text-center py-12 bg-gradient-to-r from-green-600 to-teal-600 rounded-2xl text-white">
          <Heart className="w-12 h-12 mx-auto mb-4 opacity-90" />
          <h2 className="text-3xl font-bold mb-4">
            Join Us in Building an Inclusive Web
          </h2>
          <p className="text-xl mb-8 opacity-90 max-w-3xl mx-auto">
            If you believe in a web for all, you can make a difference:
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl mx-auto mb-8 text-left px-4">
            <div className="bg-white/10 rounded-lg p-4">
              <Globe className="w-6 h-6 mb-2" />
              <p className="text-sm">Use our tool and fix issues as you find them</p>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <Github className="w-6 h-6 mb-2" />
              <p className="text-sm">Share feedback or submit new test rules via GitHub</p>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <Users className="w-6 h-6 mb-2" />
              <p className="text-sm">Promote accessibility within your organization</p>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <Heart className="w-6 h-6 mb-2" />
              <p className="text-sm">Spread the word so more webmasters commit to inclusive design</p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/"
              className="inline-flex items-center justify-center px-8 py-3 bg-white text-green-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              <Zap className="w-5 h-5 mr-2" />
              Start Free Testing
            </Link>
            <a
              href="https://github.com/inclusive-design/AChecker"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-8 py-3 bg-green-700 text-white rounded-lg font-semibold hover:bg-green-800 transition-colors border border-green-500"
            >
              <Github className="w-5 h-5 mr-2" />
              Contribute on GitHub
            </a>
          </div>
        </section>

        {/* Disclaimer / Footer */}
        <section className="bg-gray-50 rounded-lg p-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Important Information</h2>
          <div className="space-y-4 text-sm text-gray-600">
            <p>
              <strong>Disclaimer:</strong> The results from our tool are for guidance—they do not constitute legal advice. Always pair automated tools with manual review and expert auditing.
            </p>
            <p>
              <strong>License & Open Source:</strong> We use GPL-2.0 for transparency. View our source code on{' '}
              <a
                href="https://github.com/inclusive-design/AChecker"
                target="_blank"
                rel="noopener noreferrer"
                className="text-green-600 hover:text-green-700"
              >
                GitHub
              </a>.
            </p>
          </div>
        </section>
      </div>
    </>
  )
}
